import AuthContext from 'contexts/AuthContext';
import type { AuthContextType } from 'contexts/AuthContext';
import FHIRPoster from 'data/FHIRPoster';
import type { Reference } from 'fhir/r4';
import { ObservationBuilder } from 'fhir-sdoh';
import moment from 'moment';
import React, { useContext, useEffect } from 'react';
import SemanticDatepicker from 'react-semantic-ui-datepickers';
import { Form, Icon, Label, Loader, Popup, Select } from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';
import type { ResourceEntry } from 'utils/bundleNarrowing';
import { formatMaternityDisplayLabel } from 'utils/maternityCounterUtils';
import { softDeleteObservation } from 'utils/softDeleteObservation';

import type { FlatVital } from '../../../types/flat-fhir';
import * as FHIRUtils from '../../../utils/FHIRUtils';

import type { ProfileChildBaseProps, ProfileMutate, ProfileObservationEntry, RejectionWithMessage } from './types';
/**
 * `patientId` and `mutate` are required for the same reason as in `vitals.tsx`:
 * both flow unguarded into `ObservationBuilder`/`softDeleteObservation` and
 * `props.mutate().then(...)`. See the defect note there — `patientId`
 * originates as FHIR's `0..1` `Patient.id`.
 *
 * SUSPECTED DEFECT — the highlight `useEffect` below opens with
 * `props.maternityData[0]`, indexing the prop with no guard, while the render
 * body further down *does* guard it (`if (fhirMaternityHealthData)`, with an
 * `else return <></>`). The guarded render is unreachable for an absent prop:
 * the effect throws first and takes the whole profile subtree down. Reachable
 * whenever the profile query has not resolved. `maternityData.spec.tsx` pins
 * this by asserting the throw, so the prop is typed `| undefined` to state that
 * absence genuinely reaches here — narrowing it to required would misdescribe
 * the contract and break the spec that documents the bug.
 */
type MaternityHealthProps = Omit<ProfileChildBaseProps, 'patientId'> & {
  /** The member's FHIR `Patient.id`. */
  patientId: string;
  /** Maternity-coded Observation entries from `Profile.getDataByCode`. */
  maternityData: ProfileObservationEntry[] | undefined;
  /** Revalidates the profile query after an observation is added or removed. */
  mutate: ProfileMutate;
};

//props maternityData patientId sidebar mutate
function MaternityHealth(props: MaternityHealthProps) {
  // `useContext(AuthContext)` rather than `useAuthContext()`, which would be a
  // different call. The cast asserts away the `| undefined` this component has
  // always dereferenced through.
  const authInfo = useContext(AuthContext) as AuthContextType;

  const [loading, setLoading] = React.useState(!props.maternityData ? 'Loading Meternity Data...' : '');
  const [observationOption, setObservationOption] = React.useState<string | undefined>();
  const [observationDate, setObservationDate] = React.useState('');
  const [freeText, setFreeText] = React.useState('');
  const [popOpen, setpopOpen] = React.useState(false);
  const [highlightID, setHighlightID] = React.useState<string[]>([]);

  const MATERNAL_CONDITION_CODES = [
    {
      id: 'expected-due-date',
      text: 'EXPECTED_DUE_DATE',
    },
    {
      id: 'member-reported-due-date',
      text: 'MEMBER_REPORTED_DELIVERY_DATE',
    },
    {
      id: 'c-section-date',
      text: 'PLANNED_C-SECTION_DATE',
    },
    {
      id: 'pregnancy-loss',
      text: 'PREGNANCY_LOSS', // | || || |_
    },
    {
      id: 'prenatal-issue-mother',
      text: 'PRE-NATAL_ISSUE_MOTHER',
    },
    {
      id: 'prenatal-issue-baby',
      text: 'PRE-NATAL_ISSUE_BABY',
    },
    {
      id: 'postpartum-issue-mother',
      text: 'POSTPARTUM_ISSUE_MOTHER',
    },
    {
      id: 'postpartum-issue-baby',
      text: 'POSTPARTUM_ISSUE_BABY',
    },
    {
      id: 'other',
      text: 'OTHER_REASON',
    },
  ];
  const DROPDOWN_OPTIONS = MATERNAL_CONDITION_CODES.map((option) => ({
    key: option.id,
    text: option.text,
    value: option.text,
  }));

  const DATE_FORMAT: string = 'YYYY-MM-DD';

  useEffect(() => {
    const maternityData = props.maternityData!;
    if (maternityData[0]) {
      let lastDataInfo = '';
      let indexOfQuestionnaire = -1;
      let currentDataInfo: string | undefined = '';
      for (let i = 0; i < maternityData.length; i++) {
        const resource = maternityData[i]?.resource;
        //if the data does have surveyResponse id, we will use the surveyResponse id as lastDataInfo
        if ((resource?.derivedFrom?.length ?? 0) > 0) {
          indexOfQuestionnaire = -1;
          currentDataInfo = '';
          indexOfQuestionnaire = resource!['derivedFrom']!.findIndex(
            (obj: Reference) => obj.display === 'Questionnaire'
          );
          currentDataInfo = resource?.['derivedFrom']?.[indexOfQuestionnaire]?.['reference']?.split('/')?.[1];
        } else {
          //if the data does not have surveyResponse id, we will use the effectivePeriod as lastDataInfo
          currentDataInfo = resource?.effectivePeriod?.start;
        }
        if (!lastDataInfo) {
          lastDataInfo = currentDataInfo!;
        }
        if (currentDataInfo === lastDataInfo || moment(lastDataInfo).diff(currentDataInfo, 'minutes') < 1) {
          // `Observation.id` is `0..1`; `includes(undefined)` is simply false and
          // `undefined` is what lands in the highlight set for an id-less row.
          /* eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- the `?.` and the `!` guard DIFFERENT values here — the chain guards the container, the `!` an inner field FHIR leaves optional. Both are upstream's */
          if (!highlightID.includes(resource?.id!)) {
            /* eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain -- the `?.` and the `!` guard DIFFERENT values here — the chain guards the container, the `!` an inner field FHIR leaves optional. Both are upstream's */
            setHighlightID((highlightID) => [...highlightID, resource?.id!]);
          }
        } else {
          break;
        }
      }
    }
  }, [highlightID, props]);

  const changeOption = (value: string) => {
    setObservationOption(value);
  };

  const handleDateChange = (_e: React.SyntheticEvent<HTMLElement> | undefined, data: { value: Date | null }) => {
    if (data.value === null) {
      return false;
    }
    setObservationDate(moment(data.value).format(DATE_FORMAT));
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target && e.target.value;
    if (!value || value === '') {
      return false;
    }
    setFreeText(value);
  };

  const handlePopOpen = () => {
    setpopOpen(true);
  };
  const handleClose = () => {
    setpopOpen(false);
  };

  const handleDelete = (observationId: string) => {
    setLoading('Deleting Maternity Label...');
    setpopOpen(false);
    const resource = props.maternityData?.find((entry: ResourceEntry<'Observation'>) => entry?.resource?.id === observationId)?.resource;
    if (!resource) {
      setLoading('Error - Unable to delete Maternity label: observation not found');
      return;
    }
    softDeleteObservation({
      observation: resource,
      token: authInfo.token,
      patientId: props.patientId,
      practitionerId: authInfo.practitionerId,
      practitionerName: authInfo.practitionerName,
      org: props.org,
    }).then(
      () => {
        setLoading('Deletion Success. Updating Maternity UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
        });
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to delete Maternity label: ${error.message}`);
      }
    );
  };

  const handleSubmit = async (val: (React.SyntheticEvent<HTMLElement> & { preventDefault?: () => void }) | undefined, text: string) => {
    if (val?.preventDefault) {
      val.preventDefault();
    }
    setpopOpen(false);
    setLoading('Submitting Maternity Label...');

    const body: fhir4.Observation = ObservationBuilder.buildMaternityHealthObservation(
      props.patientId,
      new Date().toJSON(),
      // Same as in `consent.tsx`: unset until the dropdown is used, and the builder
      // stamps `undefined` rather than refusing the write.
      observationOption as string,
      '82810-3', //Pregnancy LOINC
      observationOption !== 'OTHER_REASON' ? (observationOption as string) : text,
      `${observationDate}`,
      authInfo.practitionerName
    );

    await FHIRPoster(`/Observation/`, authInfo.token, body).then(
      () => {
        setLoading('Submission Success. Updating Maternity UI...');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; awaiting it would order this render behind a refetch.
        props.mutate().then(() => {
          setLoading('');
        });
        setObservationOption('');
        setObservationDate('');
        setFreeText('');
      },
      (error: RejectionWithMessage) => {
        setLoading(`Error - Unable to submit Maternity label: ${error.message}`);
      }
    );
  };

  const fhirMaternityHealthData = props.maternityData;
  let flatMaternityHealthUnsorted: FlatVital[] = [];

  if (fhirMaternityHealthData) {
    flatMaternityHealthUnsorted = fhirMaternityHealthData
      .filter((fhirMaternity: ResourceEntry<'Observation'>) => fhirMaternity?.resource?.status !== 'entered-in-error')
      .map((fhirMaternity: ResourceEntry<'Observation'>) => {
        return FHIRUtils.parseFHIRVital(fhirMaternity.resource); //can be parsed as FHIRVital instead of making a new parser. Not sure which is more ideal
      });
    let flatMaternityObservations: FlatVital[] = [];
    if (flatMaternityHealthUnsorted) {
      flatMaternityObservations = flatMaternityHealthUnsorted.sort((c1, c2) => {
        return moment(c2.effectiveDate, 'YYYY-MM-DD').valueOf() - moment(c1.effectiveDate, 'YYYY-MM-DD').valueOf();
      });
    }

    return (
      <>
        {loading.length > 0 && <Loader active inline="centered" content={loading} />}
        {flatMaternityObservations.map((flatMaternity) => {
          const effectiveDate = moment(flatMaternity.effectiveDate, 'YYYY-MM-DD').format('MMMM Do, YYYY');
          return (
            <Label
              key={flatMaternity.id}
              style={{ margin: '0.15em' }}
              basic={flatMaternity.display.toLowerCase().indexOf('date') === -1 ? false : true}
              color={
                flatMaternity.display.toLowerCase().indexOf('date') === -1
                  ? 'red'
                  : highlightID?.includes(flatMaternity?.id)
                    ? 'teal'
                    : 'grey'
              }
            >
              <div style={{ display: 'flex' }}>
                <div>
                  <a target="_blank" rel="noopener noreferrer">
                    {formatMaternityDisplayLabel(flatMaternity.display, flatMaternity.readingType)}
                  </a>
                  {': '}
                  {flatMaternity.valueString?.length > 0 && <b>{flatMaternity.valueString}</b>}

                  {flatMaternity.valueCode && (
                    <a
                      href={`https://icdlist.com/?t=icd10&s=${flatMaternity.valueCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {flatMaternity.valueCode}
                    </a>
                  )}

                  <div>{effectiveDate}</div>
                </div>

                {!props.sidebar && flatMaternityObservations.length > 1 && (
                  <>
                    <div style={{ width: 10 }} />
                    <Icon
                      style={{ float: 'right' }}
                      size="large"
                      name="delete"
                      onClick={() => handleDelete(flatMaternity.id)}
                    />
                  </>
                )}
              </div>
            </Label>
          );
        })}

        {!props.sidebar && (
          <Popup
            trigger={<Label as="a">+</Label>}
            wide
            content={
              /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- the value is a `fhir-sdoh` export whose module ships no usable types */
              <Form onSubmit={(e, { value }) => handleSubmit(value, freeText)}>
                <div style={{ minWidth: '22rem', maxWidth: 'min(100vw - 2rem, 32rem)' }}>
                  <Form.Field
                    name="option"
                    label="OBSERVATION OPTION:"
                    selectOnBlur={false}
                    control={Select}
                    search
                    fluid
                    key="option"
                    placeholder="Select an option"
                    value={observationOption}
                    options={DROPDOWN_OPTIONS}
                    onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) =>
                        // Every option this control offers is a string; Semantic's
                        // `value` is the whole dropdown union.
                        changeOption(value as string)
                      }
                  />
                  {observationOption === 'OTHER_REASON' && (
                    <Form.Input
                      label="Free Text Observation"
                      placeholder="Enter Value"
                      type="text"
                      name="newobs"
                      onChange={(e) => handleTextInput(e)}
                    />
                  )}
                  <Form.Group>
                    <Form.Field
                      style={{ color: 'black', flex: '1 1 auto', minWidth: '12rem' }}
                      control={SemanticDatepicker}
                      default
                      placeholder="Enter Date Here"
                      //value={observationDate}
                      label="ESTIMATED DATE:"
                      onChange={(e: React.SyntheticEvent<HTMLElement> | undefined, data: { value: Date | null }) => handleDateChange(e, data)}
                      clearable={false}
                      size="small"
                      format={DATE_FORMAT}
                    />
                    <Form.Button
                      style={{ alignSelf: 'flex-end' }}
                      primary
                      content="Add"
                      disabled={
                        !observationOption ||
                        !observationDate ||
                        (observationOption === 'OTHER_REASON' && !freeText.trim())
                      }
                    />
                  </Form.Group>
                </div>
              </Form>
            }
            on="click"
            position="top right"
            open={popOpen}
            onOpen={handlePopOpen}
            onClose={handleClose}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
}

export default MaternityHealth;
